import {
  filterClientClosingPackageItems,
  type ClosingPackageItem,
  type ClosingPackageProjection,
} from '../types';

/**
 * Display-only Summary metrics derived from Closing Package Projection.
 * No Workflow / Task Engine / mutation side effects.
 */

export type ClosingPackageSummaryMetrics = {
  readinessReady: number;
  readinessTotal: number;
  readinessPercent: number;
  requiredReady: number;
  requiredTotal: number;
  missingRequired: number;
  blockersCount: number;
  lastDispatchAt: string | null;
  lastChangedAt: string | null;
};

function isIncluded(item: ClosingPackageItem): boolean {
  return item.compositionMeta?.compositionStatus !== 'EXCLUDED';
}

function isReady(item: ClosingPackageItem): boolean {
  return item.state !== 'MISSING';
}

function latestIso(dates: Array<string | null | undefined>): string | null {
  let best: string | null = null;
  let bestTs = Number.NEGATIVE_INFINITY;
  for (const value of dates) {
    if (!value) continue;
    const ts = Date.parse(value);
    if (Number.isNaN(ts)) continue;
    if (ts > bestTs) {
      bestTs = ts;
      best = value;
    }
  }
  return best;
}

export function buildClosingPackageSummaryMetrics(
  projection: ClosingPackageProjection
): ClosingPackageSummaryMetrics {
  const clientItems = filterClientClosingPackageItems(projection.items ?? []).filter(isIncluded);
  const requiredItems = clientItems.filter((item) => item.required);

  const readinessReady = clientItems.filter(isReady).length;
  const readinessTotal = clientItems.length;
  const readinessPercent =
    readinessTotal === 0 ? 0 : Math.round((readinessReady / readinessTotal) * 100);

  const requiredReady = requiredItems.filter(isReady).length;
  const requiredTotal = requiredItems.length;
  const missingRequired = requiredItems.filter((item) => item.state === 'MISSING').length;

  const lastDispatchAt = latestIso((projection.dispatches ?? []).map((d) => d.dispatchedAt));

  const meta = projection.workflowMetadata;
  const lastChangedAt = latestIso([
    projection.generatedAt,
    projection.formedAt,
    lastDispatchAt,
    meta?.lastApprovalAt,
    meta?.preparedAt,
    meta?.originalsDispatchedAt,
    meta?.originalsReceivedAt,
  ]);

  return {
    readinessReady,
    readinessTotal,
    readinessPercent,
    requiredReady,
    requiredTotal,
    missingRequired,
    blockersCount: (projection.blockers ?? []).length,
    lastDispatchAt,
    lastChangedAt,
  };
}

/** Compact date for KPI value (DD.MM.YYYY). */
export function formatClosingPackageSummaryDate(value: string | null | undefined): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

/** Compact datetime for «последнее изменение» (DD.MM HH:mm). */
export function formatClosingPackageSummaryDateTime(value: string | null | undefined): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function formatReadinessValue(metrics: ClosingPackageSummaryMetrics): string {
  if (metrics.readinessTotal === 0) return '—';
  if (metrics.readinessPercent === 100) return '100%';
  return `${metrics.readinessReady} / ${metrics.readinessTotal}`;
}

export function formatRequiredValue(metrics: ClosingPackageSummaryMetrics): string {
  if (metrics.requiredTotal === 0) return '—';
  return `${metrics.requiredReady} / ${metrics.requiredTotal}`;
}
